import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../auth/useAuth';
import { DEV_AUTH_BYPASS } from '../auth/devAuthFlag';
import { msalConfigured } from '../auth/msalConfig';
import { googleConfigured } from '../auth/googleConfig';
import { facebookConfigured, getFacebookAccessToken } from '../auth/facebookAuth';
import { appleConfigured, getAppleCredential } from '../auth/appleAuth';
import { microsoftConfigured } from '../auth/microsoftAuth';
import BrandLockup from '../components/BrandLockup';
import PromoBanner from '../components/PromoBanner';
import { usePromoCampaign } from '../hooks/usePromoCampaign';
import { rememberMobileReturn } from '../mobileReturn';

type PendingProvider = 'google' | 'facebook' | 'apple' | 'microsoft' | null;

/**
 * Branded sign-in splash with direct Google Identity Services credential sign-in
 * and the existing Microsoft Entra External ID hosted flow.
 */
const LoginPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading,
    authError,
    loginEmail,
    loginMicrosoft,
    loginGoogle,
    loginFacebook,
    loginApple,
  } = useAuth();
  const [googleError, setGoogleError] = useState('');
  const [pendingProvider, setPendingProvider] = useState<PendingProvider>(null);
  const { campaign: promo, loading: promoLoading } = usePromoCampaign();
  // A golfer who accepted a promo offer is signing up, not signing back in. Treat the moment the offer
  // is still being looked up the same way so the heading does not flip once the banner appears.
  const signingUpForOffer = promo !== null || promoLoading;
  const socialLoginConfigured = googleConfigured || facebookConfigured || appleConfigured || microsoftConfigured;
  const socialLoginPending = pendingProvider !== null;

  useEffect(() => {
    rememberMobileReturn(new URLSearchParams(location.search).get('mobileReturn'));
  }, [location.search]);

  const handleGoogleCredential = async (credential?: string) => {
    if (!credential) {
      setPendingProvider(null);
      setGoogleError('Google did not return a sign-in credential. Please try again.');
      return;
    }
    setPendingProvider('google');
    setGoogleError('');
    try {
      await loginGoogle(credential);
    } catch {
      setGoogleError('Google sign-in could not be completed. Please try again.');
    } finally {
      setPendingProvider(null);
    }
  };

  const handleFacebookLogin = async () => {
    setPendingProvider('facebook');
    setGoogleError('');
    try {
      await loginFacebook(await getFacebookAccessToken());
    } catch {
      setGoogleError('Facebook sign-in could not be completed. Please try again.');
    } finally {
      setPendingProvider(null);
    }
  };

  const handleAppleLogin = async () => {
    setPendingProvider('apple');
    setGoogleError('');
    try {
      const credential = await getAppleCredential();
      await loginApple(credential.identityToken, credential.nonce, credential.displayName);
    } catch {
      setGoogleError('Apple sign-in could not be completed. Please try again.');
    } finally {
      setPendingProvider(null);
    }
  };

  const handleMicrosoftLogin = async () => {
    setPendingProvider('microsoft');
    setGoogleError('');
    try {
      await loginMicrosoft();
    } catch {
      setGoogleError('Microsoft sign-in could not be completed. Please try again.');
    } finally {
      setPendingProvider(null);
    }
  };

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const returnTo = new URLSearchParams(location.search).get('returnTo');
      navigate(returnTo === '/onboarding' ? '/onboarding' : '/play', { replace: true });
    }
  }, [isAuthenticated, isLoading, location.search, navigate]);

  return (
    <div className="login-page">
      <div
        className="login-page__scene"
        aria-hidden="true"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/login-bg-2.jpg)` }}
      />
      <div className="login-page__shade" aria-hidden="true" />

      <section className="login-hero" aria-label="Sign in to CaddieSight">
        <BrandLockup tone="onDark" />

        <div className="login-card">
          {promo ? <PromoBanner campaign={promo} /> : null}
          <div className="login-card__header">
            {signingUpForOffer ? (
              <>
                <h2>Create your account</h2>
                <p>Sign up to claim your free trial. Already have an account? Sign in the same way.</p>
              </>
            ) : (
              <>
                <h2>Now on the first tee, please welcome...</h2>
                <p>Secure access to your shots, courses, and swing data.</p>
              </>
            )}
          </div>

          {DEV_AUTH_BYPASS && (
            <Button
              label="Dev sign-in (bypass Entra)"
              icon="pi pi-wrench"
              className="login-btn"
              severity="warning"
              onClick={loginEmail}
            />
          )}

          {!DEV_AUTH_BYPASS && socialLoginConfigured && (
            <div className="login-social" aria-label="Social sign-in options">
              {googleConfigured && (
                <div
                  className={`login-social__google${socialLoginPending ? ' login-social__google--disabled' : ''}`}
                  title="Continue with Google"
                  aria-disabled={socialLoginPending}
                  role={pendingProvider === 'google' ? 'status' : undefined}
                  aria-label={pendingProvider === 'google' ? 'Signing in with Google' : undefined}
                >
                  {pendingProvider === 'google' ? (
                    <i className="pi pi-spin pi-spinner login-social__glyph" aria-hidden="true" />
                  ) : (
                    <span className="login-social__glyph login-social__glyph--google" aria-hidden="true">G</span>
                  )}
                  <GoogleLogin
                    onSuccess={(response) => void handleGoogleCredential(response.credential)}
                    onError={() => {
                      setPendingProvider(null);
                      setGoogleError('Google sign-in was cancelled or unavailable.');
                    }}
                    use_fedcm_for_button={false}
                    type="icon"
                    shape="circle"
                    size="large"
                  />
                </div>
              )}
              {facebookConfigured && (
                <button
                  type="button"
                  className="login-social__button"
                  aria-label="Continue with Facebook"
                  title="Continue with Facebook"
                  onClick={() => void handleFacebookLogin()}
                  disabled={isLoading || socialLoginPending}
                >
                  {pendingProvider === 'facebook' ? (
                    <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                  ) : (
                    <span className="login-social__glyph login-social__glyph--facebook" aria-hidden="true">f</span>
                  )}
                </button>
              )}
              {appleConfigured && (
                <button
                  type="button"
                  className="login-social__button"
                  aria-label="Continue with Apple"
                  title="Continue with Apple"
                  onClick={() => void handleAppleLogin()}
                  disabled={isLoading || socialLoginPending}
                >
                  {pendingProvider === 'apple' ? (
                    <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                  ) : (
                    <i className="pi pi-apple login-social__glyph login-social__glyph--apple" aria-hidden="true" />
                  )}
                </button>
              )}
              {microsoftConfigured && (
                <button
                  type="button"
                  className="login-social__button"
                  aria-label="Continue with Microsoft"
                  title="Continue with Microsoft"
                  onClick={() => void handleMicrosoftLogin()}
                  disabled={isLoading || socialLoginPending}
                >
                  {pendingProvider === 'microsoft' ? (
                    <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                  ) : (
                    <span className="login-social__glyph login-social__glyph--microsoft" aria-hidden="true">
                      <span /><span /><span /><span />
                    </span>
                  )}
                </button>
              )}
            </div>
          )}

          {(googleError || authError) && (
            <div className="login-error" role="alert">{googleError || authError}</div>
          )}

          {!DEV_AUTH_BYPASS && socialLoginConfigured && <div className="login-divider" aria-hidden="true" />}

          <Button
            label="Continue with email"
            icon="pi pi-envelope"
            className="login-btn login-btn--email"
            onClick={loginEmail}
            disabled={isLoading}
          />

          {!DEV_AUTH_BYPASS && !msalConfigured && (
            <p className="login-note">
              Entra External ID is not configured yet — connect the tenant settings to enable sign-in.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
